import { useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Divider,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { Importer, ImporterField } from "react-csv-importer";
import "react-csv-importer/dist/index.css";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useDispatch } from "react-redux";
import { saveCSVFileAction } from "../../redux/actions/CSVImportActions";
import "./CSVImportButton.css"; // Import the extracted CSS file

// Component for rendering the progress chart
function ProgressChart({ progressData, rowCount, totalRows }) {
  if (progressData.length === 0) return null;

  return (
    <Box sx={{ width: "100%", height: 200, mt: 3 }}>
      <ResponsiveContainer>
        <LineChart data={progressData}>
          <XAxis
            dataKey="index"
            type="number"
            domain={[0, totalRows]}
            tickFormatter={(val) => val}
            tick={{ fontSize: 12, fontWeight: "bold" }}
          />
          <YAxis
            domain={[0, 100]}
            tickFormatter={(val) => `${val}%`}
            tick={{ fontSize: 12, fontWeight: "bold" }}
          />
          <Tooltip
            formatter={(value) => [`${value}%`, "Progress"]}
            contentStyle={{ fontSize: 14, fontWeight: "bold" }}
          />
          <Line
            type="monotone"
            dataKey="percent"
            stroke="#4CAF50"
            strokeWidth={3}
            dot={false}
            isAnimationActive
          />
        </LineChart>
      </ResponsiveContainer>
      <Typography variant="body2" sx={{ textAlign: "center", mt: 1 }}>
        Imported Rows: {rowCount} / {totalRows} (
        {Math.min(Math.round((rowCount / (totalRows || 1)) * 100), 100)}%)
      </Typography>
    </Box>
  );
}

// Helper function to format a date value
const formatDate = (value) => {
  const d = new Date(value);
  if (isNaN(d.getTime())) {
    const today = new Date();
    return today.toISOString().split("T")[0];
  }
  return d.toISOString().split("T")[0];
};

// Helper function to format a single row of CSV data
const formatRow = (row) => ({
  id: 0,
  firstName: row.firstName || "",
  middleName: row.middleName || "",
  lastName: row.lastName || "",
  phoneNumber: row.phoneNumber || "",
  lineOfBusiness: row.lineOfBusiness || "",
  email: row.email || "",
  type: row.type || "",
  leadSource: row.leadSource || "",
  status: row.status || "",
  quoteNumber: row.quoteNumber || "",
  businessType: row.businessType || "",
  leadMetaInformation: row.leadMetaInformation || "",
  assignedTo: Number(row.assignedTo) || 0,
  followUpDate: formatDate(row.followUpDate),
  username: row.username || "",
  doNotEmail: row.doNotEmail === "true" || false,
  sendLoginInfoByEmail: row.sendLoginInfoByEmail === "true" || false,
  notes: [
    {
      id: 0,
      message: row.noteMessage || "",
      type: row.noteType || "",
    },
  ],
});

// Main component
export default function CsvImportButton({
  fields = [],
  onChunk,
  onComplete,
  onImportSuccess,
  buttonText = "Import CSV",
  title = "Import CSV",
  helperText = "Drag & drop a CSV file here, or click to choose from your computer.",
  size = "medium",
  color = "primary",
  variant = "outlined",
  sx = {},
}) {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const allRowsRef = useRef([]);
  const [progressData, setProgressData] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [fileSelected, setFileSelected] = useState(false);

  // Reset state when opening the dialog
  const handleOpen = () => {
    allRowsRef.current = [];
    setProgressData([]);
    setRowCount(0);
    setTotalRows(0);
    setFileSelected(false);
    setOpen(true);
  };

  // Close the dialog
  const handleClose = () => setOpen(false);

  // Handle import completion
  const handleComplete = async () => {
    if (allRowsRef.current.length > 0) {
      const formattedData = allRowsRef.current.map(formatRow);

      dispatch(
        saveCSVFileAction(formattedData, (response) => {
          if (onImportSuccess) onImportSuccess();
        })
      );
    }

    setOpen(false);
  };

  // Handle importer start (after file selection and preview)
  const handleImporterStart = ({ file, preview }) => {
    allRowsRef.current = [];
    setProgressData([]);
    setRowCount(0);
    setTotalRows(preview?.rowCount || 0);
    setFileSelected(true);
  };

  // Handle processing a chunk of rows
  const handleProcessChunk = async (rows, { startIndex }) => {
    allRowsRef.current.push(...rows);
    const importedSoFar = startIndex + rows.length;
    setRowCount(importedSoFar);

    const percent =
      totalRows > 0
        ? Math.min(Math.round((importedSoFar / totalRows) * 100), 100)
        : 0;

    setProgressData((prev) => [...prev, { index: importedSoFar, percent }]);

    if (onChunk) await onChunk(rows);
  };

  // Dialog Paper styles
  const dialogPaperSx = {
    borderRadius: 3,
    overflow: "hidden",
    height: "100vh",
    display: "flex",
    flexDirection: "column",
  };

  // Dialog title styles
  const dialogTitleSx = {
    fontWeight: 700,
    pb: 1,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  };

  // Dialog content styles
  const dialogContentSx = {
    bgcolor: "background.default",
    overflowY: "auto",
    p: 3,
  };

  // Importer wrapper styles
  const importerWrapperSx = {
    p: { xs: 1, sm: 2 },
    borderRadius: 2,
    border: "1px dashed",
    borderColor: "divider",
    bgcolor: "background.paper",
    mt: 5,
  };

  // Close button styles
  const closeButtonSx = {
    color: "#000",
    "&:hover": { backgroundColor: "rgba(0,0,0,0.1)" },
  };

  // Close action button styles
  const closeActionSx = {
    textTransform: "none",
    borderRadius: 2,
    color: "#000",
    borderColor: "#ccc",
    "&:hover": {
      borderColor: "#000",
      backgroundColor: "#f9f9f9",
    },
  };

  // Dialog actions styles
  const dialogActionsSx = {
    px: 3,
    py: 2,
    justifyContent: "flex-end",
  };

  return (
    <>
      {/* Import Button */}
      <Button
        variant="contained"
        startIcon={<UploadFileIcon />}
        onClick={handleOpen}
        disabled={false} // Add disabled logic if needed, e.g., based on some state
       
      >
        {buttonText}
      </Button>

      {/* Import Dialog */}
      <Dialog
        open={open}
        onClose={(event, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") {
            return;
          }
          handleClose(event, reason);
        }}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: dialogPaperSx,
        }}
      >
        {/* Dialog Title */}
        <DialogTitle sx={dialogTitleSx}>
          {title}
          <IconButton onClick={handleClose} sx={closeButtonSx}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* Dialog Content */}
        <DialogContent dividers sx={dialogContentSx}>
          {/* Helper Text - Show only if no file selected */}
          {!fileSelected && (
            <Box sx={{ mt: 4 }}>
              <Typography variant="h5" color="text.secondary">
                {helperText}
              </Typography>
            </Box>
          )}

          {/* Divider - Show only if no file selected */}
          {!fileSelected && <Divider sx={{ mt: 4 }} />}

          {/* CSV Importer */}
          <Box className="csv-importer-wrapper" sx={{
            p: { xs: 1, sm: 2 },
            borderRadius: 2,
            border: "1px dashed",
            borderColor: "divider",
            bgcolor: "background.paper",
            height:'67%',
            mt: 5,
            '& .rcvi-mapping-container, & [class*="mapping"]': {
              display: 'grid !important',
              gridTemplateColumns: 'repeat(6, minmax(80px, 1fr)) !important', // 6 fields per row with narrow min-width
              gap: '0.5rem !important',
            },
            '& .rcvi-field-wrapper, & [class*="field"]': {
              minWidth: '80px !important',
              maxWidth: '100px !important',
              padding: '0.25rem !important',
              fontSize: '0.75rem !important',
            },
            '& .rcvi-field-wrapper:nth-child(1), & [class*="field"]:nth-child(1)': { // First Name narrower
              minWidth: '40px !important',
              maxWidth: '40px !important',
            },
            // NEW: Hide the library's built-in Import button to avoid duplication
            '& button[type="submit"], & .rcvi-next-button, & [class*="import-button"]': {
              display: 'none !important',
            }
          }}>
            
            <Importer
              assumeNoHeaders={false}
              restartable
              onStart={handleImporterStart}
              processChunk={handleProcessChunk}
              onComplete={handleComplete}
              onClose={handleClose}
              disableHeaders={false}
              allowManualMapping={true}
              requireAllFields={false}
              validateHeaders={false}
              skipEmptyLines={true}
              displayColumnPageSize={10}
            >
               <Box sx={{ textAlign: "center", mt: 2, mb: 2, px: 2 }}>
                <Typography variant="h5" color="text.secondary">
                  Drag and drop columns into fields 
                </Typography>
              </Box>
               
              <ImporterField name="firstName" label="First Name" optional />
              <ImporterField name="lastName" label="Last Name" optional />
              <ImporterField name="phoneNumber" label="Phone Number" optional />
              <ImporterField name="email" label="Email" optional />
              <ImporterField
                name="lineOfBusiness"
                label="Line Of Business"
                optional
              />
              <ImporterField name="status" label="Status" optional />
              <ImporterField
                name="assignedTo"
                label="Assigned To (ID)"
                optional
              />
              <ImporterField
                name="followUpDate"
                label="Follow Up Date"
                optional
              />
              <ImporterField name="businessType" label="Business Type" optional />
              <ImporterField
                name="premium"
                label="Premium"
                optional
                validate={(value) =>
                  isNaN(value) ? "Must be a number" : undefined
                }
              />
            
            </Importer>
          </Box>
        

          {/* Progress Chart */}
          <ProgressChart
            progressData={progressData}
            rowCount={rowCount}
            totalRows={totalRows}
          />
        </DialogContent>

        {/* Dialog Actions - UPDATED: Added custom Import button next to Close */}
        <DialogActions sx={dialogActionsSx}>
        
          <Button onClick={handleClose} variant="outlined" sx={closeActionSx}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}